<template>
  <div
    class="nav-dd"
    :class="[`nav-dd--${mode}`, { 'is-open': open }]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @focusout="onFocusOut"
  >
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="nav-dd-trigger"
      aria-haspopup="true"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="onTriggerClick"
      @keydown="onTriggerKeydown"
    >
      <span v-if="mode === 'mobile'" class="drawer-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <circle
            cx="6"
            cy="17"
            r="3.3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <circle
            cx="18"
            cy="17"
            r="3.3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="M6 17l4.5-8h4L12 17M10.5 9h4.5M14.5 9l3.5 8M9 12h4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <span class="nav-dd-trigger-label">Bike insurance</span>
      <svg
        class="nav-dd-chevron"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M6 9.5l6 6 6-6"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <ul
      v-show="open"
      :id="panelId"
      class="nav-dd-panel"
      :aria-labelledby="triggerId"
      @keydown="onPanelKeydown"
    >
      <li
        v-for="item in items"
        :key="item.to"
        :class="{ 'nav-dd-overview': item.overview }"
      >
        <nuxt-link
          ref="itemLink"
          no-prefetch
          :to="item.to"
          @click.native="onItemClick"
        >
          {{ item.label }}
        </nuxt-link>
      </li>
    </ul>
  </div>
</template>

<script>
import { BIKE_INSURANCE_LINKS } from '@/helpers/nav-links'

// The site's first dropdown nav pattern, so it's deliberately plain: a real
// <button> disclosure (aria-haspopup/aria-expanded) plus a list of links,
// not role="menu" — the WAI-ARIA authoring practices steer away from the
// menu/menuitem roles for site navigation, since those imply arrow-key-only
// interaction that fights a screen reader's normal link browsing.
//
// One component, two render modes, one shared item list (helpers/nav-links.js)
// — desktop opens on hover with a click/tap fallback and overlays without
// pushing layout; mobile is tap-only and expands inline in the drawer's
// normal flow, which is what makes it "inline" rather than a second overlay.
export default {
  name: 'BikeInsuranceNavDropdown',
  props: {
    mode: {
      type: String,
      default: 'desktop',
      validator: (value) => ['desktop', 'mobile'].includes(value),
    },
  },
  data() {
    return {
      open: false,
      items: BIKE_INSURANCE_LINKS,
      closeTimer: null,
    }
  },
  computed: {
    triggerId() {
      return `bike-insurance-nav-trigger-${this.mode}-${this._uid}`
    },
    panelId() {
      return `bike-insurance-nav-panel-${this.mode}-${this._uid}`
    },
  },
  watch: {
    // Covers both a link click inside the panel and any other navigation —
    // a stale open dropdown must never survive a route change.
    $route() {
      this.closeMenu()
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    this.clearCloseTimer()
  },
  methods: {
    // The trigger and the panel are visually separated by a margin (see
    // .nav-dd-panel), so a mouse moving from one to the other crosses a gap
    // that belongs to neither element. mouseleave fires the instant the
    // cursor enters that gap, which used to close the menu before the
    // pointer could ever reach a link. Closing on a short delay — cancelled
    // by any mouseenter within the window, including re-entering from the
    // panel side — gives an ordinary diagonal mouse path enough time to
    // land back inside the component.
    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    openMenu() {
      this.clearCloseTimer()
      this.open = true
    },
    closeMenu() {
      this.clearCloseTimer()
      this.open = false
    },
    scheduleClose() {
      this.clearCloseTimer()
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null
        this.open = false
      }, 300)
    },
    onDocumentClick(event) {
      if (this.open && !this.$el.contains(event.target)) {
        this.closeMenu()
      }
    },
    onMouseEnter() {
      if (this.mode === 'desktop') {
        this.openMenu()
      }
    },
    onMouseLeave() {
      if (this.mode === 'desktop') {
        this.scheduleClose()
      }
    },
    onTriggerClick() {
      if (this.open) {
        this.closeMenu()
      } else {
        this.openMenu()
      }
    },
    onTriggerKeydown(event) {
      if (event.key === 'Escape') {
        this.closeMenu()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.openMenu()
        this.$nextTick(this.focusFirstItem)
      }
    },
    onPanelKeydown(event) {
      const links = this.$refs.itemLink || []
      const focusedIndex = links.findIndex(
        (link) => link.$el === document.activeElement
      )

      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeMenu()
        this.$refs.trigger.focus()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        const next = links[focusedIndex + 1]
        if (next) {
          next.$el.focus()
        }
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (focusedIndex <= 0) {
          this.$refs.trigger.focus()
        } else {
          links[focusedIndex - 1].$el.focus()
        }
      }
    },
    onFocusOut(event) {
      const next = event.relatedTarget
      if (!next || !this.$el.contains(next)) {
        this.closeMenu()
      }
    },
    onItemClick() {
      this.closeMenu()
    },
    focusFirstItem() {
      const first = (this.$refs.itemLink || [])[0]
      if (first) {
        first.$el.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.nav-dd {
  position: relative;
}

.nav-dd-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.nav-dd-chevron {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  transition: transform 120ms ease;
}

.nav-dd.is-open .nav-dd-chevron {
  transform: rotate(180deg);
}

// --- Desktop: matches the sibling .main-link items in MainNavigation.vue,
// duplicated here (rather than depending on that component's scoped class)
// because Vue's scoped styles don't cross a component boundary. ------------
.nav-dd--desktop {
  .nav-dd-trigger {
    padding: 0 14px;
    height: 100%;
    font-size: 17px;
    font-weight: 500;
    color: $ps-body-color;
    transition: color 120ms ease;

    &:hover {
      color: $ps-primary;
    }
  }

  &.is-open .nav-dd-trigger {
    color: $ps-primary;
    font-weight: 600;
  }

  .nav-dd-panel {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 30;
    min-width: 260px;
    margin-top: 8px;
    padding: 8px;
    border-radius: $ps-radius-md;
    background: $ps-white;
    box-shadow: 0 12px 32px rgba(20, 22, 31, 0.14);

    li + li {
      margin-top: 2px;
    }

    a {
      display: block;
      padding: 10px 12px;
      border-radius: $ps-radius-sm;
      font-size: $ps-text-small;
      font-weight: 500;
      color: $ps-body-color;
      white-space: nowrap;

      &:hover,
      &:focus-visible {
        background: $ps-primary-50;
        color: $ps-primary;
      }
    }

    .nav-dd-overview {
      margin-bottom: 6px;
      padding-bottom: 8px;
      border-bottom: 1px solid $ps-border-color;

      a {
        font-weight: 700;
        color: $ps-ink;
      }
    }
  }
}

// --- Mobile: matches the sibling rows in MainNavigation.vue's drawer, same
// duplication reasoning as above. Expands inline (static position) rather
// than overlaying, per the "no second overlay" requirement. ----------------
.nav-dd--mobile {
  .nav-dd-trigger {
    width: 100%;
    padding: 10px 2px;
    color: $white;
  }

  .nav-dd-trigger-label {
    flex: 1 1 auto;
    text-align: left;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
  }

  .drawer-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: $ps-radius-md;
    background: rgba(255, 255, 255, 0.14);
    color: $white;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .nav-dd-chevron {
    color: rgba(255, 255, 255, 0.55);
  }

  &.is-open .nav-dd-chevron {
    color: $white;
  }

  .nav-dd-panel {
    position: static;
    padding: 2px 0 6px 50px;

    li + li {
      margin-top: 2px;
    }

    a {
      display: block;
      padding: 9px 8px;
      border-radius: $ps-radius-sm;
      font-size: $ps-text-small;
      color: rgba(255, 255, 255, 0.85);

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.12);
        color: $white;
      }
    }

    .nav-dd-overview {
      margin-bottom: 4px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.18);

      a {
        font-weight: 700;
        color: $white;
      }
    }
  }
}
</style>
